import React, { useState } from 'react';

// constellation
import { Button, IconListOutline, IconMapOutline } from '@zillow/constellation';

// components
import ZestimateSummary from '../ZestimateSummary';
import FilterModule from '../FilterModule';
import CompsCarousel from '../CompsCarousel';
import Map from '../Map';
import ListingCard from '../ListingCard';
import DownloadPDFButton from '../DownloadPDFButton';
import RentRangeChart from '../RentRangeChart';

// flux
import { useAppSelector } from '../flux/configureStore';

// misc/utils
import { currencyStringToInt } from '../utils/numberUtils';
import * as S from './styles';

// types
import type Comparables from '../models/Comparables';

export interface PriceMyRentalComponentProps {
  heading?: React.ReactNode;
}

function getPriceOrZestimate(comparable: Comparables, zestimate: number): number {
  if (comparable.isUsersComp) {
    return zestimate;
  }
  return currencyStringToInt(comparable.bubblePrice || '');
}

const PriceMyRentalComponent: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<PriceMyRentalComponentProps>>
> = (props) => {
  const { heading } = props;

  const { comparables, address, app } = useAppSelector((state) => state);
  const { device, googleMapsApiKey } = app;
  const { isMobile } = device;

  const [isMapOpen, setIsMapOpen] = useState(!isMobile);
  const [isLoading, setIsLoading] = useState(false);
  const activeId: string = useAppSelector((state) => state.ui.activeId);
  const activeComp = comparables.items.find((comp: Comparables) => comp.listingId === activeId);
  const priceFromFilter = useAppSelector((state) => state.filter.price);

  const isCompsCarouselOpen = isMobile ? !isMapOpen : true;
  const isListingCardOpen = isMobile && isMapOpen && !activeComp?.isUsersComp;
  const areAppValuesSet = !!googleMapsApiKey && !!device;
  const { rentZestimate } = address;
  const userPropertyPrice = priceFromFilter || rentZestimate;

  const handleMapOrListButtonClick = () => setIsMapOpen(!isMapOpen);

  return (
    <>
      {heading}
      <S.Flex marginBottom="md">
        {address.street !== null && <ZestimateSummary />}
        {!isMobile && <DownloadPDFButton width={100} />}
      </S.Flex>
      {comparables.items.length > 1 && (
        <RentRangeChart
          upperBound={getPriceOrZestimate(comparables.items[comparables.items.length - 1], userPropertyPrice)}
          lowerBound={getPriceOrZestimate(comparables.items[0], userPropertyPrice)}
          zestimate={userPropertyPrice}
        />
      )}
      <S.Content isLoading={isLoading}>
        {comparables.items.length > 0 && (
          <S.ActionsContainer>
            <FilterModule setIsLoading={setIsLoading} />
            {isMobile && (
              <>
                <Button buttonType="secondary" onClick={handleMapOrListButtonClick} marginLeft="xs">
                  {isMapOpen ? (
                    <>
                      <IconListOutline marginRight="xs" /> List
                    </>
                  ) : (
                    <>
                      <IconMapOutline marginRight="xs" /> Map
                    </>
                  )}
                </Button>
                <DownloadPDFButton />
              </>
            )}
          </S.ActionsContainer>
        )}
        {isMapOpen && (
          <S.Spacer marginBottom={isMobile ? null : 'sm'} ignorePadding={isMobile}>
            <S.MapContainer>{areAppValuesSet && <Map useBubble={!isMobile} />}</S.MapContainer>
            {!!activeComp && isListingCardOpen && <ListingCard listing={activeComp} />}
          </S.Spacer>
        )}
        <CompsCarousel isShowing={isCompsCarouselOpen} />
      </S.Content>
    </>
  );
};

export default PriceMyRentalComponent;
